import React from "react";
import { LoadScript } from "@react-google-maps/api";
import "regenerator-runtime/runtime.js";

import MapComponent from "./map/map";
import SearchAndFilter from "./search-and-filter/search-and-filter";
import Results from "./results/results";

const libraries = ["places"];

class BusinessLocator extends React.Component {
  constructor(props) {
    super(props);
    this.placesResults = React.createRef();
    this.state = {
      services: this.props.data.services,
      offices: this.props.data.offices.sort((a, b) =>
          this.sortAlphabetically(a, b)
      ),
      filteredOffices: null,
      apiKey: this.props.data.googleMapsApiKey,
      mapId: this.props.data.mapId,
      language: this.props.data.culture,
      region: this.props.data.culture.substring(
          this.props.data.culture.indexOf("-")
      ),
      defaultZoom: this.props.data.zoom,
      detailedZoom: this.props.data.detailedZoom,
      zoom: this.props.data.zoom,
      defaultCenterLatitude: this.props.data.centerLatitude,
      defaultCenterLongitude: this.props.data.centerLongitude,
      locationZoomoffsetY: this.props.data.locationZoomoffsetY,
      locationZoomoffsetX: this.props.data.locationZoomoffsetX,
      center: {
        lat: this.props.data.centerLatitude,
        lng: this.props.data.centerLongitude,
      },
      searchTerm: this.props.searchTerm,
      currentPlace: null,
      selectedService: this.props.selectedService,
      selected: this.props.selectedOfficeId,
    };
  }

  async componentDidMount() {
    if (typeof window !== "undefined") {
      window.onpopstate = () => {
        if (history.state !== null) {
          this.setState(history.state);
        }
      };
    }
  }

  sortAlphabetically(officeA, officeB) {
    if (officeA.name.toLowerCase() < officeB.name.toLowerCase()) return -1;
    if (officeA.name.toLowerCase() > officeB.name.toLowerCase()) return 1;
    return 0;
  }

  sortOnDistance(officeA, officeB) {
    return officeA.distance - officeB.distance;
  }

  updateOfficeMarkerLocations = async () => {
    let { offices, filteredOffices, selected, currentPlace } = this.state;

    for (let office of offices) {
      if (office.placeId) {
        let service = new google.maps.places.PlacesService(
            this.placesResults.current
        );

        await new Promise((resolve) => {
          service.getDetails({ placeId: office.placeId }, (result) => {
            if (result) {
              let location = result.geometry.location;

              if (location) {
                office.latitude = location.lat();
                office.longitude = location.lng();
              }

              resolve();
            } else {
              resolve();
            }
          });
        });
      }
    }

    let selectedOfficeData = this.getSelectedOfficeData(
        this.getSelectedOffice(
            filteredOffices ? filteredOffices : offices,
            selected,
            currentPlace
        )
    );

    this.setState(
        {
          offices,
          ...selectedOfficeData,
        },
        () => history.replaceState(this.state, "", this.buildQueryString())
    );
  };

  handleSearchChange = (event) => {
    this.setState({ searchTerm: event.target.value });
  };

  handleServiceFilterChange = (event) => {
    this.setState({ selectedService: event.target.value });
  };

  handleDistanceMatrixResponses = (responses, offices) => {
    if (!responses && responses[0]) {
      return null;
    }

    let responseRows = [];
    responses.forEach((response) => {
      if (response.rows.length > 0) {
        responseRows = responseRows.concat(response.rows[0].elements);
      }
    });

    let response = {
      originAddress: responses[0].originAddresses[0],
      elements: responseRows,
    };

    let foundAddress = response.originAddress;
    if (response.elements.length <= 0) {
      return null;
    }

    let distanceMatrixElements = response.elements;
    offices.forEach((office, index) => {
      let distanceMatrixElement = distanceMatrixElements[index];

      if (
          distanceMatrixElement.status === "NOT_FOUND" ||
          distanceMatrixElement.status === "ZERO_RESULTS"
      ) {
        office.distance = null;
        return;
      }

      office.distance = distanceMatrixElement.distance.value;
    });

    return {
      foundAddress,
      offices,
    };
  };

  handleSearch = async (event) => {
    if (event) {
      event.preventDefault();
    }

    let { previousSearchTerm, searchTerm, offices, currentPlace, selected } =
        this.state;

    if (!searchTerm) {
      currentPlace = null;

      offices.forEach((office) => {
        office.distance = null;
      });

      offices.sort((a, b) => this.sortAlphabetically(a, b));
    } else {
      var distanceMatrixResult = await this.getDistanceMatrixResponses(
          previousSearchTerm,
          searchTerm,
          offices,
          currentPlace
      );

      if (!distanceMatrixResult) {
        offices.forEach((office) => {
          office.distance = null;
        });

        currentPlace = null;
      } else {
        offices = distanceMatrixResult.offices;
        currentPlace = distanceMatrixResult.foundAddress;
      }

      if (offices[0] && offices[0].distance) {
        offices = offices.sort((a, b) => this.sortOnDistance(a, b));
      }
    }

    var filteredOffices = this.applyServiceFiltering(offices);

    let selectedOfficeData = this.getSelectedOfficeData(
        this.getSelectedOffice(
            filteredOffices ? filteredOffices : offices,
            event ? 0 : selected,
            currentPlace
        )
    );

    this.setState(
        {
          previousSearchTerm: searchTerm,
          searchTerm,
          currentPlace,
          offices,
          filteredOffices,
          ...selectedOfficeData,
        },
        () => history.pushState(this.state, "", this.buildQueryString())
    );
  };

  toggleSelected = (office) => {
    let selectedOfficeData = this.getSelectedOfficeData(office);

    this.setState(
        {
          ...selectedOfficeData,
        },
        () => history.pushState(this.state, "", this.buildQueryString())
    );
  };

  applyServiceFiltering(offices) {
    let { selectedService } = this.state;

    if (!selectedService || selectedService === "-") {
      return null;
    }

    return offices.filter((office) =>
        office.availableServices.includes(selectedService)
    );
  }

  async getDistanceMatrixResponses(
      previousPlaceSearchTerm,
      currentPlaceSearchTerm,
      offices,
      currentPlace
  ) {
    if (
        !currentPlaceSearchTerm ||
        currentPlaceSearchTerm === previousPlaceSearchTerm
    ) {
      return {
        foundAddress: currentPlace,
        offices,
      };
    }

    var chunkSize = 25;

    // Offices need to be split into chunks of 25 because of the distance matrix MAX_DIMENSIONS_EXCEEDED (https://developers.google.com/maps/documentation/javascript/distancematrix#response-status-codes)
    const officesSplitInChunks = offices.reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / chunkSize);

      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = []; // start a new chunk
      }

      resultArray[chunkIndex].push(item);

      return resultArray;
    }, []);

    var promises = officesSplitInChunks.map((officeChunk) => {
      var service = new google.maps.DistanceMatrixService();

      return service.getDistanceMatrix({
        origins: [currentPlaceSearchTerm],
        destinations: officeChunk.map((office) => ({
          lng: office.longitude,
          lat: office.latitude,
        })),
        travelMode: "DRIVING",
      });
    });

    var results = await Promise.all(promises);

    return this.handleDistanceMatrixResponses(results, offices);
  }

  getSelectedOfficeData(selectedOffice) {
    let {
      detailedZoom,
      defaultZoom,
      defaultCenterLatitude,
      defaultCenterLongitude,
      locationZoomoffsetY,
      locationZoomoffsetX,
    } = this.state;

    let zoom = defaultZoom;
    let center = {
      lat: defaultCenterLatitude,
      lng: defaultCenterLongitude,
    };

    if (selectedOffice) {
      const map = this.mapRef;
      if (map && map.getProjection()) {
        center = this.getNewCenter(
            map,
            selectedOffice,
            detailedZoom,
            locationZoomoffsetX,
            locationZoomoffsetY
        );

        map.setZoom(detailedZoom);
      } else {
        center = {
          lat: selectedOffice.latitude,
          lng: selectedOffice.longitude,
        };
      }

      zoom = detailedZoom;
    }

    return { selected: selectedOffice?.id ?? 0, center, zoom };
  }

  getNewCenter(map, selectedOffice, zoom, offsetX, offsetY) {
    const newOfficeCenter = {
      lat: selectedOffice.latitude,
      lng: selectedOffice.longitude,
    };

    const scale = Math.pow(2, zoom);

    const currentCenterPoint = map
        .getProjection()
        .fromLatLngToPoint(newOfficeCenter);

    const pixelOffset = new google.maps.Point(
        offsetX / scale || 0,
        offsetY / scale || 0
    );

    const newCenterPoint = new google.maps.Point(
        currentCenterPoint.x - pixelOffset.x,
        currentCenterPoint.y + pixelOffset.y
    );

    const centerLatLng = map.getProjection().fromPointToLatLng(newCenterPoint);

    return {
      lat: centerLatLng.lat(),
      lng: centerLatLng.lng(),
    };
  }

  getSelectedOffice(offices, selected, currentPlace) {
    if (selected > 0) {
      return offices.filter((office) => office.id === selected)[0];
    }

    if (!currentPlace) {
      return null;
    }

    return offices[0];
  }

  buildQueryString() {
    let { searchTerm, selectedService, selected } = this.state;

    let path = window ? window.location.pathname : "";
    let searchParams = new URLSearchParams("");

    if (searchTerm) {
      searchParams.append("place", searchTerm);
    }

    if (selectedService && selectedService !== "-") {
      searchParams.append("service", selectedService);
    }

    if (selected) {
      searchParams.append("selected", selected);
    }

    const {otherQueryParameters} = this.props.data;

    if (otherQueryParameters) {
      for (const [key, value] of Object.entries(otherQueryParameters)) {
        searchParams.append(key, value);
      }
    }

    if ([...searchParams].length <= 0) {
      return window ? window.location.pathname : "";
    }

    return path + "?" + searchParams.toString();
  }

  render() {
    const { resourcedTexts } = this.props;
    const {
      offices,
      filteredOffices,
      searchTerm,
      services,
      selectedService,
      selected,
      apiKey,
      mapId,
      region,
      language,
      center,
      zoom,
    } = this.state;

    if (offices === null) {
      return <div className="loading">{resourcedTexts.noDataFound}</div>;
    }

    return (
        <section className="business-locator">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <SearchAndFilter
                    services={services}
                    resourcedTexts={resourcedTexts}
                    onSubmit={this.handleSearch}
                    searchTerm={searchTerm}
                    searchOnChange={this.handleSearchChange}
                    selectedService={selectedService}
                    serviceOnChange={this.handleServiceFilterChange}
                />
              </div>
            </div>
          </div>

          <div className="business-locator__map-container">
            <div className="hidden" ref={this.placesResults} />
            <div className="business-locator__wrapper">
              <Results
                  offices={filteredOffices ? filteredOffices : offices}
                  searchTerm={searchTerm}
                  resourcedTexts={resourcedTexts}
                  selected={selected}
                  toggleSelected={this.toggleSelected}
              />

              <LoadScript
                  googleMapsApiKey={apiKey}
                  region={region}
                  language={language}
                  libraries={libraries}
              >
                <MapComponent
                    onLoad={(map) => {
                      this.mapRef = map;
                      this.updateOfficeMarkerLocations().catch(console.error);
                      this.handleSearch().catch(console.error);
                    }}
                    mapId={mapId}
                    offices={filteredOffices ? filteredOffices : offices}
                    center={center}
                    zoom={zoom}
                    resourcedTexts={resourcedTexts}
                    selected={selected}
                    toggleSelected={this.toggleSelected}
                />
              </LoadScript>
            </div>
          </div>
        </section>
    );
  }
}

export { BusinessLocator };
